import { create } from 'zustand';

interface AppLogger { error(message: string, cause?: unknown): void; }
let _logger: AppLogger = { error: (msg, cause) => console.error(msg, cause) };
export function configureBreathworksLogger(logger: AppLogger): void { _logger = logger; }
import { dev, filterBreathworks } from '@breath-lab/domain';
import {
  type Activity,
  type Breathwork,
  type BreathworkDifficultyLevel,
  type Filters,
  type TrainingBenefit,
  emptyFilters,
} from '@breath-lab/types';
import { getServices } from '@breath-lab/services';

interface BreathworksState {
  breathworks: Breathwork[];
  activities: Activity[];
  benefits: TrainingBenefit[];
  freeIds: string[];
  paywallEnabled: boolean;
  isLoaded: boolean;
  /**
   * Tracks which fetcher last hydrated the store. `loadPublic` returns lighter
   * PublicBreathworks (no configurationUi); after sign-in the auth-aware
   * `load()` must re-fetch even though `isLoaded` is already true.
   */
  loadedScope: 'public' | 'auth' | null;
  isLoading: boolean;
  error: boolean;
  query: string;
  filters: Filters;

  // derived
  filteredBreathworks: () => Breathwork[];

  // actions
  load: () => Promise<void>;
  loadPublic: () => Promise<void>;
  setQuery: (query: string) => void;
  toggleActivity: (id: string) => void;
  toggleBenefit: (id: string) => void;
  toggleDifficulty: (level: BreathworkDifficultyLevel) => void;
  clearFilters: () => void;
}

export const useBreathworks = create<BreathworksState>((set, get) => ({
  breathworks: [],
  activities: [],
  benefits: [],
  freeIds: [],
  paywallEnabled: false,
  isLoaded: false,
  loadedScope: null,
  isLoading: false,
  error: false,
  query: '',
  filters: emptyFilters(),

  filteredBreathworks: () => {
    const { breathworks, activities, benefits, query, filters } = get();
    return filterBreathworks(breathworks, query, filters, activities, benefits);
  },

  load: async () => {
    if (get().loadedScope === 'auth') return;
    set({ isLoading: true, error: false });
    try {
      const { data, remoteConfig } = getServices();
      const [breathworks, activities, benefits, { freeBreathworksIds, paywallEnabled }] = await Promise.all([
        data.fetchBreathworks(),
        data.fetchActivities(),
        data.fetchBenefits(),
        remoteConfig.fetchCatalogGates(),
      ]);
      dev.log('[RemoteConfig] paywallEnabled:', paywallEnabled);
      dev.log('[RemoteConfig] freeBreathworksIds:', freeBreathworksIds);
      const usedActivityIds = new Set(breathworks.flatMap((b) => b.activitiesIds));
      const usedBenefitIds = new Set(breathworks.flatMap((b) => b.benefitsIds));
      const usedActivities = activities.filter((a) => usedActivityIds.has(a.id));
      const usedBenefits = benefits.filter((b) => usedBenefitIds.has(b.id));
      set({ breathworks, activities: usedActivities, benefits: usedBenefits, freeIds: freeBreathworksIds, paywallEnabled: true, isLoaded: true, loadedScope: 'auth', isLoading: false });
    } catch (e) {
      _logger.error('Failed to load breathworks', e);
      set({ isLoading: false, error: true });
    }
  },

  loadPublic: async () => {
    if (get().loadedScope !== null) return;
    set({ isLoading: true, error: false });
    try {
      const { data, remoteConfig } = getServices();
      const [breathworks, activities, benefits, { freeBreathworksIds, paywallEnabled }] = await Promise.all([
        data.fetchBreathworksPublic(),
        data.fetchActivities().catch(() => []),
        data.fetchBenefits(),
        remoteConfig.fetchCatalogGates(),
      ]);
      const usedActivityIds = new Set(breathworks.flatMap((b) => b.activitiesIds));
      const usedBenefitIds = new Set(breathworks.flatMap((b) => b.benefitsIds));
      const usedActivities = activities.filter((a) => usedActivityIds.has(a.id));
      const usedBenefits = benefits.filter((b) => usedBenefitIds.has(b.id));
      dev.log('[RemoteConfig] paywallEnabled:', paywallEnabled);
      dev.log('[RemoteConfig] freeBreathworksIds:', freeBreathworksIds);
      set({ breathworks, activities: usedActivities, benefits: usedBenefits, freeIds: freeBreathworksIds, paywallEnabled: true, isLoaded: true, loadedScope: 'public', isLoading: false });
    } catch {
      set({ isLoading: false, error: true });
    }
  },

  setQuery: (query) => set({ query }),

  toggleActivity: (id) => {
    set((s) => {
      const current = s.filters.selectedActivitiesIds;
      const updated = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { filters: { ...s.filters, selectedActivitiesIds: updated } };
    });
  },

  toggleBenefit: (id) => {
    set((s) => {
      const current = s.filters.selectedBenefitIds;
      const updated = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { filters: { ...s.filters, selectedBenefitIds: updated } };
    });
  },

  toggleDifficulty: (level) => {
    set((s) => {
      const current = s.filters.selectedDifficulties;
      const updated = current.includes(level) ? [] : [level];
      return { filters: { ...s.filters, selectedDifficulties: updated } };
    });
  },

  clearFilters: () => set({ query: '', filters: emptyFilters() }),
}));
